import { FC, useMemo } from "react";

import { QuestionResults, Section } from "@/types/resources";
import TURFReport, { TURFReportData } from "./TURFReport";
import { itemsFromQuestion } from "./_itemsFromQuestion";

interface TURFSectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/** Greedy unduplicated-reach computation: at each step pick the item
    that brings in the largest number of respondents not yet covered.
    The function returns both the size→reach curve (in count units)
    and the canonical "best portfolio" of size `targetSize`. */
function greedyTURF(
  selectionMatrix: boolean[][],
  itemCount: number,
  targetSize: number
): { portfolio: number[]; reachByCount: number[] } {
  const respondentCount = selectionMatrix.length;
  const remaining = new Set<number>(
    Array.from({ length: itemCount }, (_, i) => i)
  );
  const reached = new Array<boolean>(respondentCount).fill(false);
  const portfolio: number[] = [];
  const reachByCount: number[] = [];

  for (let pick = 0; pick < itemCount; pick += 1) {
    let bestIdx = -1;
    let bestGain = -1;
    remaining.forEach((idx) => {
      let gain = 0;
      for (let r = 0; r < respondentCount; r += 1) {
        if (!reached[r] && selectionMatrix[r]?.[idx]) gain += 1;
      }
      if (gain > bestGain) {
        bestGain = gain;
        bestIdx = idx;
      }
    });
    if (bestIdx === -1) break;
    remaining.delete(bestIdx);
    if (pick < targetSize) {
      portfolio.push(bestIdx);
    }
    for (let r = 0; r < respondentCount; r += 1) {
      if (!reached[r] && selectionMatrix[r]?.[bestIdx]) reached[r] = true;
    }
    const reachedCount = reached.reduce((s, v) => s + (v ? 1 : 0), 0);
    reachByCount.push(reachedCount);
  }

  return { portfolio, reachByCount };
}

/**
 * TURF sections are templated as a single multi-choice question
 * whose options (text or one image each) are the candidate items
 * ("Which would you buy?"). We:
 *
 * 1. Build a respondent × item selection matrix from `multiple_choice`
 *    answers.
 * 2. Run the greedy reach algorithm to produce reach-by-portfolio-size.
 * 3. Pick the elbow as the first size where marginal lift drops below
 *    `ELBOW_THRESHOLD_PP`. That size's greedy pick is the recommended
 *    portfolio.
 *
 * Falls back to a flat reach curve / item-list portfolio when the
 * section has no usable answers, so the chart still renders.
 */
function cookTURFData(data: QuestionResults[] | undefined): TURFReportData {
  const choiceQuestion = (data ?? []).find(
    (r) => r?.question?.type === "multiple_choice"
  );
  const items = itemsFromQuestion(choiceQuestion?.question).map((item) => ({
    id: item.id,
    name: item.name,
  }));

  const respondents: boolean[][] = [];
  if (choiceQuestion) {
    (choiceQuestion.groups ?? []).forEach((g) => {
      (g.answers ?? []).forEach((a) => {
        if (a.answer.type !== "multiple_choice") return;
        const row = items.map(() => false);
        (a.answer.choices ?? []).forEach((c) => {
          if (typeof c === "number" && c >= 0 && c < row.length) row[c] = true;
        });
        respondents.push(row);
      });
    });
  }

  const sampleSize = respondents.length;
  const perItemCounts = items.map((_, idx) =>
    respondents.reduce((s, row) => s + (row[idx] ? 1 : 0), 0)
  );

  const ELBOW_THRESHOLD_PP = 2; // marginal lift cutoff in percentage points
  let elbowSize = items.length;
  let reachBySize: number[];

  if (sampleSize === 0 || items.length === 0) {
    reachBySize = items.map(() => 0);
    return {
      elbowPortfolio: items.map((_, idx) => idx).slice(0, elbowSize),
      items,
      perItemCounts,
      reachBySize,
      sampleSize,
    };
  }

  const fullRun = greedyTURF(respondents, items.length, items.length);
  reachBySize = fullRun.reachByCount.map((c) => (c / sampleSize) * 100);

  for (let k = 1; k < reachBySize.length; k += 1) {
    if (reachBySize[k] - reachBySize[k - 1] < ELBOW_THRESHOLD_PP) {
      elbowSize = k; // size k (1-indexed) is the last one with meaningful lift
      break;
    }
  }
  if (elbowSize === 0) elbowSize = Math.min(1, items.length);

  const elbowRun = greedyTURF(respondents, items.length, elbowSize);

  return {
    elbowPortfolio: elbowRun.portfolio,
    items,
    perItemCounts,
    reachBySize,
    sampleSize,
  };
}

/* ── Component ──────────────────────────────────────────── */

const TURFSectionResults: FC<TURFSectionResultsProps> = ({ data }) => {
  const reportData: TURFReportData = useMemo(() => cookTURFData(data), [data]);
  return <TURFReport data={reportData} />;
};

export default TURFSectionResults;
